import { CampaignSummaryItem } from './operationsDashboardUtils';

export type KpiFilterKey =
  | 'scheduled'
  | 'present'
  | 'late'
  | 'absent'
  | 'covered'
  | 'uncovered'
  | 'covering'
  | 'readiness'
  | 'load';

const KPI_FILTER_LABELS: Record<KpiFilterKey, string> = {
  scheduled: 'All scheduled',
  present: 'Present (on time)',
  late: 'Present (late)',
  absent: 'Absent',
  covered: 'Covered positions',
  uncovered: 'Uncovered positions',
  covering: 'Covering others',
  readiness: 'Staffing risk campaigns',
  load: 'Elevated coverage load',
};

/** Mirrors backend resolveEmployeePeopleAttendance for KPI row filtering. */
export function resolveEmployeePeopleAttendance(employeeRows: any[]): {
  isPresentOnTime: boolean;
  isLate: boolean;
  isAbsent: boolean;
  isNoShow: boolean;
} {
  const hasClockIn = employeeRows.some(
    (row) =>
      Boolean(row.clockInTime)
      || row.attendanceStatus === 'PRESENT'
      || row.attendanceStatus === 'LATE'
      || row.attendanceStatus === 'SLIGHTLY_LATE',
  );
  const hasLate = employeeRows.some(
    (row) =>
      row.attendanceStatus === 'LATE'
      || row.attendanceStatus === 'SLIGHTLY_LATE',
  );
  const hasNoShow = employeeRows.some((row) => row.attendanceStatus === 'NO_CALL_NO_SHOW');
  const hasAbsent = employeeRows.some((row) => row.attendanceStatus === 'ABSENT');
  const hasScheduledOff = employeeRows.some((row) => row.attendanceStatus === 'SCHEDULED_OFF');

  if (hasClockIn) {
    return {
      isPresentOnTime: !hasLate,
      isLate: hasLate,
      isAbsent: false,
      isNoShow: false,
    };
  }

  if (hasNoShow) {
    return { isPresentOnTime: false, isLate: false, isAbsent: false, isNoShow: true };
  }

  if (hasAbsent || hasScheduledOff) {
    return { isPresentOnTime: false, isLate: false, isAbsent: true, isNoShow: false };
  }

  return { isPresentOnTime: false, isLate: false, isAbsent: false, isNoShow: false };
}

export function toggleKpiFilter(current: KpiFilterKey, next: KpiFilterKey): KpiFilterKey {
  if (current === next && next !== 'scheduled') return 'scheduled';
  return next;
}

export function getKpiFilterLabel(filter: KpiFilterKey): string | null {
  if (filter === 'scheduled') return null;
  return KPI_FILTER_LABELS[filter] || null;
}

export function filterRowsByKpi(
  rows: any[],
  filter: KpiFilterKey,
  campaignSummary: CampaignSummaryItem[] = [],
): any[] {
  if (filter === 'scheduled') return rows;

  if (filter === 'uncovered') {
    return rows.filter((row) => row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage);
  }

  if (filter === 'covered') {
    return rows.filter(
      (row) =>
        ['COVERED', 'PARTIALLY_COVERED'].includes(row.coverageStatus)
        && (row.requiresCoverage || row.coverageAssignmentId),
    );
  }

  if (filter === 'covering') {
    const coveringIds = new Set(
      rows.filter((row) => row.isCoveringOthers).map((row) => row.employeeId),
    );
    return rows.filter((row) => coveringIds.has(row.employeeId));
  }

  if (filter === 'load') {
    const overloadedIds = new Set(
      rows.filter((row) => (row.coveringCount ?? 0) >= 2).map((row) => row.employeeId),
    );
    return rows.filter((row) => overloadedIds.has(row.employeeId));
  }

  if (filter === 'readiness') {
    const atRiskCampaigns = new Set(
      campaignSummary
        .filter((campaign) => campaign.scheduled > 0 && campaign.operationalScore < 100)
        .map((campaign) => campaign.campaignId),
    );
    if (atRiskCampaigns.size === 0) return [];
    return rows.filter((row) => atRiskCampaigns.has(row.campaignId));
  }

  const byEmployee = new Map<string, any[]>();
  rows.forEach((row) => {
    const list = byEmployee.get(row.employeeId) || [];
    list.push(row);
    byEmployee.set(row.employeeId, list);
  });

  const matchingIds = new Set<string>();
  byEmployee.forEach((employeeRows, employeeId) => {
    const state = resolveEmployeePeopleAttendance(employeeRows);
    if (filter === 'present' && state.isPresentOnTime) matchingIds.add(employeeId);
    if (filter === 'late' && state.isLate) matchingIds.add(employeeId);
    if (filter === 'absent' && state.isAbsent) matchingIds.add(employeeId);
  });

  return rows.filter((row) => matchingIds.has(row.employeeId));
}
